import { ExternalLink, Users } from "lucide-react";
import type { RouteCanonicalItem, RouteFindingEvidence } from "@iamu/db";
import { FindingRow } from "@/components/experience/finding-row";
import { PriorityChip } from "@/components/triage/priority-chip";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { PageRouteCleanEmptyState } from "./pages-empty-states";
import { cn } from "@/lib/utils";
import type { FindingType, Severity } from "@/components/finding-badges";

export type { RouteCanonicalItem, RouteFindingEvidence };

interface RouteFindingsListProps {
  routeKey: string;
  samplePageUrl: string | null;
  stepCount: number;
  canonicals: RouteCanonicalItem[];
  selectedCanonicalId: string | null;
  onSelectCanonical: (id: string) => void;
  showAllStates: boolean;
  onToggleShowAllStates: (show: boolean) => void;
  className?: string;
}

export function RouteFindingsList({
  routeKey,
  samplePageUrl,
  stepCount,
  canonicals,
  selectedCanonicalId,
  onSelectCanonical,
  showAllStates,
  onToggleShowAllStates,
  className,
}: RouteFindingsListProps) {
  return (
    <div className={cn("flex flex-col gap-4", className)}>
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border/60 pb-3">
        <div>
          <div className="flex items-center gap-2">
            <h2 className="font-mono text-base font-semibold text-foreground">
              {routeKey}
            </h2>
            <span className="text-xs text-muted-foreground tabular-nums">
              · {stepCount} step{stepCount === 1 ? "" : "s"}
            </span>
          </div>

          {samplePageUrl && (
            <div className="mt-1 flex items-center gap-1.5 text-xs text-muted-foreground">
              <span className="truncate max-w-md">{samplePageUrl}</span>
              <a
                href={samplePageUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-foreground inline-flex items-center gap-0.5"
                title="Open sample URL"
              >
                <ExternalLink className="h-3 w-3" />
              </a>
            </div>
          )}
        </div>

        <div className="flex items-center gap-2">
          <Switch
            id="show-all-states"
            checked={showAllStates}
            onCheckedChange={onToggleShowAllStates}
          />
          <Label
            htmlFor="show-all-states"
            className="text-xs text-muted-foreground cursor-pointer"
          >
            Include resolved
          </Label>
        </div>
      </div>

      {canonicals.length === 0 ? (
        <PageRouteCleanEmptyState routeKey={routeKey} />
      ) : (
        <div className="rounded-lg border border-border/80 bg-card overflow-hidden shadow-xs dark:shadow-[inset_0_1px_0_0_oklch(1_0_0_/_0.06),0_6px_24px_-6px_oklch(0_0_0_/_0.4)]">
          <div className="hidden items-center justify-between border-b border-border/80 bg-muted/40 px-3.5 py-2 text-xs font-medium text-muted-foreground sm:flex">
            <span>Finding</span>
            <span>Observed</span>
          </div>
          <div className="divide-y divide-border/60">
            {canonicals.map((c) => {
              const isSelected = c.id === selectedCanonicalId;
              const uiFact =
                c.evidence?.uiFact && c.evidence.uiFact !== c.title
                  ? c.evidence.uiFact
                  : null;
              const consequence = c.evidence?.consequence ?? null;
              const previewText =
                uiFact && consequence
                  ? `${uiFact} — ${consequence}`
                  : (consequence ?? uiFact);

              return (
                <div
                  key={c.id}
                  onClick={() => onSelectCanonical(c.id)}
                  className={cn(
                    "flex flex-col gap-1.5 py-3 px-3 transition-colors text-left cursor-pointer",
                    isSelected ? "bg-muted/60" : "hover:bg-muted/30",
                  )}
                >
                  <FindingRow
                    variant="compact"
                    priority={
                      <PriorityChip
                        canonicalId={c.id}
                        priority={{
                          priorityScore: c.priorityScore,
                          priorityOverride: c.priorityOverride ?? null,
                        }}
                      />
                    }
                    finding={{
                      id: c.id,
                      title: c.title,
                      description: c.evidence?.uiFact ?? c.title,
                      type: c.type as FindingType,
                      severity: c.severity as Severity,
                      state: c.state,
                      observationCount: c.personas.length,
                    }}
                    onSelect={() => onSelectCanonical(c.id)}
                  />

                  {previewText && (
                    <div className="pl-6 text-xs text-muted-foreground line-clamp-2">
                      {previewText}
                    </div>
                  )}

                  <div className="flex items-center justify-between pl-6 text-xs text-muted-foreground">
                    <div className="flex items-center gap-1.5">
                      <Users className="h-3 w-3 shrink-0" />
                      <span>
                        Observed by {c.personas.map((p) => p.name).join(", ")}
                      </span>
                    </div>

                    {c.evidence && (
                      <span className="tabular-nums">
                        Step {c.evidence.stepNumber}
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
